import { BrowserRouter } from 'react-router-dom';
import React from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider } from '@apollo/client';
import createBrowserApolloClient from '@sm/apollo-clients/dist/browser';
import { L10nProvider } from '@sm/intl';
import { WrenchTheme } from '@wds/styles';
import merge from 'lodash.merge';

import './entry.scss';

import { getClientEnvironmentDetails } from '@sm/utils';
import {
  ErrorBoundary,
  FiveHundredErrorPage,
  HelmetProvider,
  StaticProvider,
  GlobalThemeProvider,
} from '@sm/webassets';

import useTheme from './pages/Pricing/hooks/useTheme';
import possibleTypes from './helpers/possibleTypes';
import { typePolicies } from './helpers/clientTypePolicies';
import ContentWebApp from './App';

const allClientStaticData = getClientEnvironmentDetails().isBrowser ? window.SM.__LOAD_PAYLOAD_CACHE__ || {} : {};

/**
 * Our servers inject the payload with the `user` details and, rightfully so, leave out the authentication attribute.
 * Hence, the client/webs need to calculate whether the user is authenticated or not by looking at the userId in the webs.
 * Now, we don't want every app/page to do this; hence, we abstract the calculation out.
 * In addition, we do not want the attribute readily available in the `window` object; hence, we
 * calculate it here and directly add it to the context.
 * Yes, the user can still access it (debugging JS/React Dev Tools) but this way, it's not readily exposed.
 *
 * With this in place, the webs can retrieve the authentication status via the context.
 */
allClientStaticData.user = allClientStaticData.user || {};
allClientStaticData.user.isAuthenticated = allClientStaticData.user.id && allClientStaticData.user.id !== '1';
const {
  environment,
  pageRequestId,
  'client-config': { appName, appVersion, graphQLUri },
  gql: { gqlCache = {} } = {},
} = allClientStaticData;

const apolloClient = createBrowserApolloClient({
  uri: graphQLUri,
  cacheHydration: gqlCache,
  pageRequestId,
  possibleTypes,
  linkOptions: {
    credentials: 'include',
    batchInterval: 30,
  },
  typePolicies: typePolicies,
  metadata: { appName, appVersion },
  availableLoggedOutPaths: [],
});

export default function RenderedApp({ localeMessages: messages }) {
  const pricingTheme = useTheme();

  /**
   * Merge any user defined theme objects
   * See http://storybook.jungle.tech/wrench/?path=/story/intro-theming--page
   */
  const globalTheme = merge({}, WrenchTheme, pricingTheme);

  return (
    <GlobalThemeProvider theme={globalTheme}>
      <ErrorBoundary FallbackComponent={FiveHundredErrorPage}>
        <HelmetProvider>
          <ApolloProvider client={apolloClient}>
            <BrowserRouter>
              <StaticProvider value={{ ...allClientStaticData }}>
                <L10nProvider
                  localeMessages={messages}
                  localeCode={environment.slLanguageLocale}
                  languageCode={environment.languageCode}
                >
                  <ContentWebApp />
                </L10nProvider>
              </StaticProvider>
            </BrowserRouter>
          </ApolloProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </GlobalThemeProvider>
  );
}

RenderedApp.defaultProps = {
  localeMessages: null,
};

RenderedApp.propTypes = {
  localeMessages: PropTypes.shape(),
};
